import { Col, Row, Text } from 'components';
import { approveModerationLog, getFlaggedContent, rejectModerationLog, theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { CATEGORY_LABELS, MODERATION_CONTENT_TYPES } from '../constants';
import ContentModal from './ContentModal';
import FlaggedCard from './FlaggedCard';
import Pagination from './Pagination';
import { AccessDenied, EmptyState, ErrorState, LoadingBlock } from './ui';

const DEFAULT_LIMIT = 20;
const TAB = 'flagged';

const selectCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-2.5 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';

const FlaggedTab = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven view state.
  const contentType = searchParams.get('contentType') || 'all';
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const appliedKey = searchParams.toString();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null); // { id, outcome } currently being actioned
  const [viewLogId, setViewLogId] = useState(null);
  const reqIdRef = useRef(0);

  const fetchFlagged = async (type, p, lim) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const res = await getFlaggedContent({ contentType: type, page: p, limit: lim });
    if (reqId !== reqIdRef.current) return;

    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load the review queue.');
      setLoading(false);
      return;
    }
    setItems(res.data.items || []);
    setPagination(res.pagination || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchFlagged(contentType, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  const writeParams = (updates) => {
    const next = { tab: TAB };
    searchParams.forEach((v, k) => {
      next[k] = v;
    });
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === 'all') delete next[k];
      else next[k] = String(v);
    });
    setSearchParams(next, { replace: true });
  };

  const total = pagination?.total ?? 0;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  const handleReview = async (item, outcome) => {
    if (busy) return;
    setBusy({ id: item._id, outcome });
    const call = outcome === 'approved' ? approveModerationLog : rejectModerationLog;
    const res = await call(item._id);
    setBusy(null);

    if (res?.status === 403) {
      setForbidden(true);
      return;
    }
    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Review failed.' } });
      return;
    }

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: res.msg || `Content ${outcome}.` }
    });

    // Optimistically drop the reviewed card.
    setItems((prev) => prev.filter((i) => i._id !== item._id));
    setPagination((prev) => (prev ? { ...prev, total: Math.max(0, (prev.total || 1) - 1) } : prev));
  };

  if (forbidden) return <AccessDenied />;

  return (
    <Col noFlex wid="100%" style={{ maxWidth: '1100px' }}>
      {/* Filter bar */}
      <div className="bg-white border border-[#D2D8DF] rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide">
              Content type
            </label>
            <select
              value={contentType}
              onChange={(e) => writeParams({ contentType: e.target.value, page: 1 })}
              className={selectCls}
            >
              <option value="all">All types</option>
              {MODERATION_CONTENT_TYPES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <Row center noFlex style={{ marginLeft: 'auto', alignSelf: 'flex-end', height: '34px' }}>
            <Text c1 col={theme.GREY_60} style={{ fontSize: '12px' }}>
              {total} awaiting review
            </Text>
          </Row>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchFlagged(contentType, page, limit)} />
      ) : loading ? (
        <LoadingBlock height="220px" />
      ) : !items.length ? (
        <EmptyState
          title="Queue is clear"
          subtitle={
            contentType === 'all'
              ? 'No flagged content is awaiting review.'
              : `No flagged ${CATEGORY_LABELS[contentType] || contentType} content is awaiting review.`
          }
        />
      ) : (
        <>
          <Col noFlex wid="100%" gap="12px">
            {items.map((item) => (
              <FlaggedCard
                key={item._id}
                item={item}
                busy={busy?.id === item._id ? busy.outcome : null}
                onApprove={(i) => handleReview(i, 'approved')}
                onReject={(i) => handleReview(i, 'rejected')}
                onViewContext={(i) => setViewLogId(i._id)}
              />
            ))}
          </Col>
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={limit}
              total={total}
              pageSizeOptions={[10, 20, 50]}
              onPageSizeChange={(size) => writeParams({ limit: size, page: 1 })}
              onPrev={() => writeParams({ page: page - 1 })}
              onNext={() => writeParams({ page: page + 1 })}
              onGoToPage={(p) => writeParams({ page: p })}
            />
          )}
        </>
      )}

      <ContentModal logId={viewLogId} onClose={() => setViewLogId(null)} />
    </Col>
  );
};

export default FlaggedTab;
